import { useEffect, useState } from "react";
import { useDraft } from "../../hooks/useDraft";
import { DraftRefusedError, type DraftRefusal, useSubmitAnswer } from "../../hooks/useQueues";
import { KEY_HINT_INLINE, shortcut } from "../../lib/platformKeys";
import { SAVED_ANSWER_LABEL } from "../../lib/reviewLines";
import { CoveredByHolderRow, coveredYesHeldByEdit, useCoveredRowHolder } from "./CoveredByHolderRow";
import { asString, cardKeyAct, type HandOffState, type Pick } from "./shared";
import type { components } from "../../api/types";

type Node = components["schemas"]["Node"];

// ---- Authority reconciliation fork ----------------------------------
//
// 0129-T7 purpose-built fork for an authority reconciliation question
// (fields.authority_conformance_dimension = "scope" | "moat"). This is
// deliberately NOT the generic designed-fork pick behavior:
//   - On a scope question the fork offers three choices: BOTH_STAND (0168: the
//     decisions and the line both stand), KEEP and REVISE; on a labelled one
//     served as awaiting the covered-by ruling it offers two, KEEP and REVISE
//     (0168 OQ6). On a moat question it offers KEEP and REVISE.
//   - The founder picks one explicitly; that CHOICE, not the pick value and not
//     an answer-string match, becomes `selected_pick` (AUTHORITY_PICK_*), the
//     deterministic server routing key (0125 Fork #6).
//   - Scope rows read AUTHORITY_SCOPE_LABELS (the planner's labels, pinned by
//     T31); moat rows read the stored picks' labels.
//   - The pick VALUES differ by dimension: for scope the KEEP value is the
//     KEEP sentinel; for moat it is the CURRENT value-core text (0129-T6),
//     which we surface as an editable field and send as `answer`.
//   - The sentinel therefore NEVER lands in an answer field (the bug 0127
//     called out): `answer` carries the edited moat text (moat KEEP) or the
//     optional note (every other choice), kept SEPARATE from the routing
//     choice.
//   - An answered question with no saved pick shows its saved answer once
//     (0168 D-18; in the covered-by row while that row offers Yes) and selects
//     no row.
export function AuthorityForkPanel({
  productId,
  node,
  dimension,
  keepPick,
  revisePick,
  bothStandPick,
  bothStandOffered,
  isCollision,
  onActNext,
  coveredHolder,
  hasSavedDraft = false,
  answerUnderReview = false,
  blocksContinue = true,
  handOff = "unknown",
  keyActsHeld = false,
  onRefused,
}: {
  productId: string;
  node: Node;
  dimension: "scope" | "moat";
  keepPick?: Pick;
  revisePick?: Pick;
  // 0168: the stored BOTH_STAND pick, found by value in QuestionPanel's split.
  bothStandPick?: Pick;
  // 0168 R-j: QuestionPanel's offer predicate: the scope dimension, less a
  // labelled question served as awaiting the covered-by ruling (0168 OQ6, ruled
  // 2026-10-06).
  bothStandOffered: boolean;
  // 0168 FD-4: QuestionPanel's collision marker (a non-blank entailed_obligation_label).
  isCollision: boolean;
  // 0051 S1c: QuestionPanel's act-advance - a saved fork choice and this
  // instance's covered-by Yes move on through it, so the page knows the move
  // was an act's.
  onActNext: () => void;
  // 0148 A16: the holder QuestionPanel offered (the authority instance of the
  // covered-by row is the only one an authority question renders).
  coveredHolder?: Node;
  // 0148 A26: passed through, because this instance renders the saved-draft
  // line too.
  hasSavedDraft?: boolean;
  // 0051 S1b: QuestionPanel's served under-review flag (the saved state reads it).
  answerUnderReview?: boolean;
  // 0051 S1b: QuestionPanel's served trigger-blocking flag (the holds-up line reads it).
  blocksContinue?: boolean;
  // 0051 S1b: QuestionPanel's hand-off state (the holds-up line shows only on "open").
  handOff?: HandOffState;
  // 0148 A40: QuestionPanel's captured hold, so the fork's Cmd/Ctrl+Enter is
  // held with it.
  keyActsHeld?: boolean;
  // 0159 F8: QuestionPanel's onRefused, for the fork's own submit hook (this
  // question its item) and this instance of the covered-by row.
  onRefused: (refusal: DraftRefusal) => void;
}) {
  const submit = useSubmitAnswer(productId, { item: node, onRefused });
  const covered = useCoveredRowHolder(coveredHolder);
  // 0168 OQ6: the covered-by row offers Yes (it renders, and no amend 2xx has
  // collapsed it to its saved line).
  const rowOffersYes = covered.holder !== undefined && !covered.amended;

  // Rehydrate from the server-persisted answer so revisiting an already-
  // answered authority question (Previous-navigation) shows the founder's
  // saved choice + edited value core, and a re-submit doesn't silently revert
  // it (LWW). delta_applier persists both fields.selected_pick and
  // fields.answer, so `choice` follows the saved pick until the founder picks
  // on this card, and the editable text is seeded from the answer, mirroring
  // the ordinary path's serverAnswer round-trip.
  const fields = (node.fields ?? {}) as Record<string, unknown>;
  const savedSelectedPick = asString(fields.selected_pick);
  const savedAnswer = asString(fields.answer);
  // The moat field owns the saved answer only for a moat KEEP; in every other
  // answered case (scope KEEP / BOTH_STAND / any REVISE / no saved pick) the
  // saved answer is the rationale.
  const savedIsMoatKeep = dimension === "moat" && savedSelectedPick === AUTHORITY_PICK_KEEP;
  const moatInitial =
    dimension === "moat"
      ? savedIsMoatKeep && savedAnswer !== ""
        ? savedAnswer
        : keepPick?.value ?? ""
      : "";
  const rationaleInitial = savedIsMoatKeep ? "" : savedAnswer;

  // The saved choice: one of the exact sentinels this question offers. A saved
  // BOTH_STAND is restored only where its row is offered (bothStandOffered: a
  // scope question, less a labelled one served as awaiting the covered-by
  // ruling); on a moat question the row does not render and the draft judge
  // refuses that pick. The suggested flag NEVER seeds the choice (FD-4: never
  // pre-selected).
  const savedChoice: AuthorityChoice | null =
    savedSelectedPick === AUTHORITY_PICK_KEEP ||
    savedSelectedPick === AUTHORITY_PICK_REVISE ||
    (bothStandOffered && savedSelectedPick === AUTHORITY_PICK_BOTH_STAND)
      ? (savedSelectedPick as AuthorityChoice)
      : null;
  // 0051 S1b: the fork shows the saved choice until the founder picks on this
  // card, so a refetch that changes the saved choice or its offer shows while
  // the card stays mounted.
  const [picked, setPicked] = useState<AuthorityChoice | null | undefined>(undefined);
  const choice = picked === undefined ? savedChoice : picked;
  // 0051 S1b: the choice the panel acts on. `choice` keeps a pick made on this
  // card, but the Both stand row can hide while the card is mounted (a queue
  // refetch serves the question as awaiting the covered-by ruling, 0168 OQ6),
  // and a hidden row never stays picked: no row shows pressed, Save & next and
  // Cmd/Ctrl+Enter post nothing. When the row comes back (the marker clears
  // while the card is mounted), the founder's own earlier pick comes back with
  // it unless another row was picked meanwhile, its save state unchanged;
  // nothing posts without Save & next or Cmd/Ctrl+Enter. Every read below goes
  // through liveChoice.
  const liveChoice: AuthorityChoice | null = choice === AUTHORITY_PICK_BOTH_STAND && !bothStandOffered ? null : choice;
  // 0168 D-18: an answered question with no saved choice (a written ruling and
  // no pick) shows its saved answer, so it never looks unanswered. A saved
  // choice already shows its row pressed and its text in the note or moat
  // field. The block selects nothing and never enables Save. While the
  // covered-by row offers Yes it plays the same saved answer back, so the block
  // waits until the row collapses (0051 S1b).
  const showSavedAnswer = savedChoice === null && savedAnswer.trim() !== "";
  // MOAT KEEP: the current value core (the KEEP pick's value, 0129-T6) or the
  // founder's saved edit on revisit. Editable; the (possibly edited) text is
  // sent as `answer`. useDraft mirrors the ordinary answer path: a localStorage
  // safety-net plus commit-on-save across the queue-refetch window.
  const [moatDraft, setMoatDraft, , commitMoat] = useDraft(
    productId,
    `${node.id}-authority-moat`,
    moatInitial,
  );
  // Free-text rationale for a scope KEEP, a BOTH_STAND, any REVISE, or the
  // saved answer of a question with no saved pick (D-18) — kept separate from
  // `choice` so an edited rationale can never mis-route the pick.
  const [rationale, setRationale, , commitRationale] = useDraft(
    productId,
    `${node.id}-authority-rationale`,
    rationaleInitial,
  );
  // The panel's own text, which the covered-by row gates Yes on (Yes rules on
  // the saved answer), and whether the marked hint may point at Yes: only while
  // the row offers Yes and does not hold it for an unsaved edit
  // (coveredYesHeldByEdit).
  const coveredLocalAnswer = savedIsMoatKeep ? moatDraft : rationale;
  const hintPointsAtYes = rowOffersYes && !coveredYesHeldByEdit(coveredLocalAnswer, node);

  const moatKeep = dimension === "moat" && liveChoice === AUTHORITY_PICK_KEEP;
  const answer = moatKeep ? moatDraft.trim() : rationale.trim();
  // Only gate on having picked. A moat KEEP with a blank value-core field is
  // allowed: the backend (resolveAuthorityMoatKeepInPlace) treats an empty moat
  // answer as "own the current value core as-is" and confirms — so disabling
  // Save here would strand the founder against valid backend semantics.
  const canSubmit = liveChoice !== null && !submit.isPending;
  // 0051 S1b: the one saved state, as on the question card - the server holds
  // this question's save (its id is in the served drafted or under-review set)
  // and the panel shows exactly it: the saved choice and the saved text.
  const saved =
    (hasSavedDraft || answerUnderReview) &&
    savedChoice !== null &&
    liveChoice === savedChoice &&
    answer === savedAnswer.trim() &&
    !submit.isPending;

  const handleSubmit = () => {
    if (!canSubmit || liveChoice === null) return;
    submit.mutate(
      { questionId: node.id, answer, selectedPick: liveChoice },
      {
        onSuccess: () => {
          // Commit the posted text synchronously so a Previous-back inside the
          // refetch window restores it before the server value arrives (same
          // safety-net the QuestionPanel save uses).
          if (moatKeep) commitMoat(answer);
          else commitRationale(answer);
          onActNext();
        },
      },
    );
  };

  // Local Cmd/Ctrl+Enter submits the fork. The generic QuestionPanel handler
  // is suppressed in authority mode so this is the only submit path. The act
  // is read through cardKeyAct (shared.tsx), the one spelling Review's keyboard
  // guard reads too.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (cardKeyAct(e) === "submit") {
        e.preventDefault();
        // 0148 A40: a data-swapped panel skips the act until a click inside it.
        if (!keyActsHeld) handleSubmit();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  // 0168 FD-4: scope rows read the planner's labels, never the stored picks (the
  // new KEEP label shows on questions minted before 0168 too). Moat rows keep
  // their stored labels; REVISE has one label in both dimensions.
  const keepLabel =
    dimension === "scope"
      ? AUTHORITY_SCOPE_LABELS.KEEP
      : keepPick?.label || AUTHORITY_MOAT_KEEP_LABEL;
  const reviseLabel =
    dimension === "scope"
      ? AUTHORITY_SCOPE_LABELS.REVISE
      : revisePick?.label || AUTHORITY_SCOPE_LABELS.REVISE;

  return (
    <div className="mt-6">
      {dimension === "moat" && (
        <p className="mb-2 font-mono text-[10px] uppercase tracking-widest text-ink-400">
          Reconcile · value core
        </p>
      )}

      {rowOffersYes && !blocksContinue && node.mvp === "include" && handOff === "open" && (
        <p data-testid="authority-holds-up" className="mb-4 break-words text-[13px] text-ink-400 leading-relaxed">
          {AUTHORITY_HOLDS_UP_LINE}
        </p>
      )}

      {showSavedAnswer && !rowOffersYes && (
        <div
          data-testid="authority-saved-answer"
          className="mb-4 rounded-lg border border-ink-700 bg-ink-900/40 px-5 py-3"
        >
          <p className="mb-1 font-mono text-[10px] uppercase tracking-widest text-ink-500">
            {SAVED_ANSWER_LABEL}
          </p>
          <p className="whitespace-pre-wrap text-[13px] leading-relaxed text-ink-200">
            {savedAnswer}
          </p>
        </div>
      )}

      {/* 0148: the SOLE covered-by row of an authority question, above the
          fork rows. The marker adds Yes and amend; on a labelled scope
          question it also hides Both stand (0168 OQ6, ruled 2026-10-06,
          amending 0148 design 4.2 (b)). */}
      {covered.holder && (
        <CoveredByHolderRow
          key={covered.holder.id}
          productId={productId}
          node={node}
          holder={covered.holder}
          localAnswer={coveredLocalAnswer}
          onUseSavedAnswer={() =>
            savedIsMoatKeep ? setMoatDraft(savedAnswer) : setRationale(savedAnswer)
          }
          onActNext={onActNext}
          hasSavedDraft={hasSavedDraft}
          amended={covered.amended}
          onAmendSaved={covered.onAmendSaved}
          onRefused={onRefused}
        />
      )}

      <div className={covered.holder ? "mt-6 space-y-2" : "space-y-2"}>
        {bothStandOffered && (
          <ForkChoiceRow
            label={AUTHORITY_SCOPE_LABELS.BOTH_STAND}
            description={
              isCollision
                ? AUTHORITY_BOTH_STAND_COLLISION_DESCRIPTION
                : AUTHORITY_BOTH_STAND_DESCRIPTION
            }
            suggested={bothStandPick?.suggested === true}
            selected={liveChoice === AUTHORITY_PICK_BOTH_STAND}
            disabled={submit.isPending}
            onClick={() => setPicked(AUTHORITY_PICK_BOTH_STAND)}
          />
        )}
        <ForkChoiceRow
          label={keepLabel}
          description={
            dimension === "scope"
              ? "Keep the decision and drop the boundary it crosses."
              : "Keep the decision and update the value core to match it."
          }
          selected={liveChoice === AUTHORITY_PICK_KEEP}
          disabled={submit.isPending}
          onClick={() => setPicked(AUTHORITY_PICK_KEEP)}
        />
        <ForkChoiceRow
          label={reviseLabel}
          description="Revise the decision so it stays inside what you committed to."
          selected={liveChoice === AUTHORITY_PICK_REVISE}
          disabled={submit.isPending}
          onClick={() => setPicked(AUTHORITY_PICK_REVISE)}
        />
      </div>

      {moatKeep && (
        <div className="mt-6">
          <label className="font-mono text-[10px] uppercase tracking-widest text-ink-400 mb-2 block">
            Current value core · edit to match your decision
          </label>
          <div
            className={`rounded-lg border ${
              submit.isPending
                ? "border-ink-700"
                : "border-ink-700 hover:border-ink-600 focus-within:border-accent/60"
            } bg-ink-900/60 transition-colors`}
          >
            <textarea
              rows={5}
              value={moatDraft}
              onChange={(e) => setMoatDraft(e.target.value)}
              disabled={submit.isPending}
              placeholder="Describe the value core you're keeping."
              className="w-full resize-none bg-transparent px-5 py-4 text-base text-ink-100 placeholder:text-ink-500 focus:outline-none disabled:opacity-60"
            />
          </div>
        </div>
      )}

      {liveChoice !== null && !moatKeep && (
        <div className="mt-6">
          <label className="font-mono text-[10px] uppercase tracking-widest text-ink-400 mb-2 block">
            {liveChoice === AUTHORITY_PICK_BOTH_STAND
              ? "Anything to add? (optional, kept with your ruling)"
              : liveChoice === AUTHORITY_PICK_KEEP
                ? "Why keep it? (optional)"
                : "How should it change? (optional)"}
          </label>
          <div
            className={`rounded-lg border ${
              submit.isPending
                ? "border-ink-700"
                : "border-ink-700 hover:border-ink-600 focus-within:border-accent/60"
            } bg-ink-900/60 transition-colors`}
          >
            <textarea
              rows={3}
              value={rationale}
              onChange={(e) => setRationale(e.target.value)}
              disabled={submit.isPending}
              placeholder={
                liveChoice === AUTHORITY_PICK_BOTH_STAND && !isCollision
                  ? AUTHORITY_BOTH_STAND_PLACEHOLDER
                  : "Plain English is fine."
              }
              className="w-full resize-none bg-transparent px-5 py-3 text-sm text-ink-100 placeholder:text-ink-500 focus:outline-none disabled:opacity-60"
            />
          </div>
        </div>
      )}

      <div
        data-testid="authority-act-row"
        className="mt-6 flex flex-wrap items-center justify-between gap-3"
      >
        <span className="font-mono text-[11px] text-ink-400">
          {liveChoice === null ? (
            hintPointsAtYes ? AUTHORITY_MARKED_HINT : "Pick one to continue"
          ) : saved ? (
            "Saved"
          ) : (
            <span className={KEY_HINT_INLINE}>{`${shortcut("↵")} to save & continue`}</span>
          )}
        </span>
        <button
          type="button"
          onClick={handleSubmit}
          disabled={!canSubmit}
          className="ml-auto inline-flex items-center gap-2 rounded-md bg-accent px-3.5 py-1.5 text-sm font-medium text-ink-950 hover:bg-accent-glow transition-colors disabled:cursor-not-allowed disabled:opacity-40"
        >
          {submit.isPending ? "saving…" : "Save & next"}
          <span className="opacity-60">→</span>
        </button>
      </div>

      {submit.error && !(submit.error instanceof DraftRefusedError) && (
        <p className="mt-4 text-sm text-danger">
          Couldn't save: {(submit.error as Error).message}
        </p>
      )}
    </div>
  );
}

// A selectable fork row (one of Both stand / Keep / Revise). Radio-style (only
// one active), styled like the question card's AnswerOption so the fork reads as
// part of the same answering surface; it toggles a choice and writes no answer text.
// `suggested` (0168 FD-4, the stored pick's PickOption.Suggested) adds the
// app's "recommended" badge; it is a marker, never a selection.
function ForkChoiceRow({
  label,
  description,
  suggested = false,
  selected,
  disabled,
  onClick,
}: {
  label: string;
  description: string;
  suggested?: boolean;
  selected: boolean;
  disabled: boolean;
  onClick: () => void;
}) {
  const border = selected
    ? "border-accent ring-2 ring-accent/60"
    : "border-ink-700 hover:border-ink-500";
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-pressed={selected}
      className={`group w-full text-left rounded-lg border ${border} bg-ink-900/40 transition-all disabled:opacity-50 disabled:cursor-not-allowed`}
    >
      <div className="flex items-start gap-4 px-5 py-4">
        <span
          className={`mt-0.5 inline-block h-3.5 w-3.5 rounded-full border shrink-0 ${
            selected ? "border-accent bg-accent" : "border-ink-500"
          }`}
        />
        {suggested && (
          <span
            data-testid="fork-choice-suggested"
            className="mt-0.5 font-mono text-[9px] uppercase tracking-widest text-accent shrink-0 inline-flex items-center gap-1.5"
          >
            <span className="inline-block h-1.5 w-1.5 rounded-full bg-accent" />
            recommended
          </span>
        )}
        <span className="flex-1">
          <span className="block text-[14px] leading-relaxed text-ink-100">
            {label}
          </span>
          {description && (
            <span className="block mt-1 text-[12px] leading-relaxed text-ink-500">
              {description}
            </span>
          )}
        </span>
      </div>
    </button>
  );
}

// The authority-reconciliation pick sentinels and the scope fork's row labels.
// They mirror planner.AuthorityPick{Keep,Revise,BothStand} and the labels DECIDE
// stamps (planner.AuthorityPickKeepScopeLabel, AuthorityPickReviseLabel,
// AuthorityPickBothStandLabel, AuthorityPickKeepMoatLabel). Trace-invariant T31
// (orchestrator/internal/contract/trace_invariants/t31_test.go) pins each
// literal below against its Go constant. REVISE and BOTH_STAND are found by
// value. The KEEP pick is the pick that is neither, because its value differs by
// dimension (the KEEP sentinel for scope, the current moat text for moat, 0129-T6).
const AUTHORITY_PICK_KEEP = "KEEP";
const AUTHORITY_PICK_REVISE = "REVISE";
const AUTHORITY_PICK_BOTH_STAND = "BOTH_STAND";

type AuthorityChoice =
  | typeof AUTHORITY_PICK_KEEP
  | typeof AUTHORITY_PICK_REVISE
  | typeof AUTHORITY_PICK_BOTH_STAND;

// 0168 FD-4: a scope question's rows read these labels, never its stored picks,
// so a question minted before 0168 (two stored picks) shows the same three rows
// with the same words as a new one.
const AUTHORITY_SCOPE_LABELS: Record<AuthorityChoice, string> = {
  BOTH_STAND: "Both stand: the decision fits inside the boundary",
  KEEP: "Keep the decision, drop the boundary",
  REVISE: "Revise the decision to conform",
};

// A moat question keeps its stored labels. This is the KEEP row's fallback when
// the stored pick carries none.
const AUTHORITY_MOAT_KEEP_LABEL = "Keep the decision (own the moat)";

// 0168 FD-4 descriptions of the both-stand row. An ordinary question resolves in
// place; a collision-born question (fields.entailed_obligation_label) routes the
// same pick to the 0146 T6 ruling arm, which drafts a ruling for approval.
const AUTHORITY_BOTH_STAND_DESCRIPTION =
  "Nothing changes. Your decisions stay as approved, the line stays in your out-of-scope list as yours, and you won't be asked about these decisions against it again.";
const AUTHORITY_BOTH_STAND_COLLISION_DESCRIPTION =
  "Nothing is retired. Your decisions stay as approved, the line stays in your out-of-scope list as yours, and the engine writes up your ruling for you to approve.";

// 0168 FD-3 (a): on an ordinary question the note typed on "both stand" is
// stored with the ruling, and no later stage reads it, so its placeholder says the
// note is kept and changes nothing.
const AUTHORITY_BOTH_STAND_PLACEHOLDER =
  "Plain English is fine. It is kept with your ruling and does not change your decisions.";

// 0168 Open question 6 (ruled 2026-10-06): the marked card (the covered-by row
// offered on an authority question) names both ways to settle it, and, while the
// spec is not handed off, says what it holds up: the hand-off, not Continue.
// Once handed off it says nothing.
const AUTHORITY_MARKED_HINT = "Answer with Yes above, or pick one here.";
const AUTHORITY_HOLDS_UP_LINE = "Only the hand-off waits on this.";

export function splitAuthorityPicks(picks: Pick[]): {
  keepPick?: Pick;
  revisePick?: Pick;
  bothStandPick?: Pick;
} {
  const revisePick = picks.find((p) => p.value === AUTHORITY_PICK_REVISE);
  const bothStandPick = picks.find((p) => p.value === AUTHORITY_PICK_BOTH_STAND);
  const keepPick = picks.find((p) => p !== revisePick && p !== bothStandPick);
  return { keepPick, revisePick, bothStandPick };
}
